import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Send, Trash2 } from "lucide-react";
import { api, errCode } from "../../lib/api";
import { useLang } from "../../lib/i18n";
import { useMaster, nameOf } from "../../lib/master";
import { fmtDateTime } from "../../lib/time";
import { PageTitle, Inp, Sel } from "../../components/Common";

const Policy = () => {
  const { t, te, lang } = useLang();
  const [vers, setVers] = useState([]);
  const [f, setF] = useState({ target_net_kg: 120, tolerance_under_kg: "", tolerance_over_kg: "", notes: "" });
  const load = () => api.get("/policy/versions").then((r) => setVers(r.data));
  useEffect(() => { load(); }, []);
  const num = (v) => (v === "" ? null : Number(v));
  const save = () => api.post("/policy", { ...f, target_net_kg: Number(f.target_net_kg), tolerance_under_kg: num(f.tolerance_under_kg), tolerance_over_kg: num(f.tolerance_over_kg) })
    .then(() => { toast.success(t("saved")); load(); }).catch((e) => toast.error(te(errCode(e))));
  const cur = vers[0];
  return (
    <section className="card p-4 space-y-3" data-testid="policy-section">
      <h2 className="font-bold text-lg">{t("policy_title")}</h2>
      {cur && <p className="text-sm" data-testid="policy-current">{t("version")} {cur.version}: {t("target")} {cur.target_net_kg} kg · −{cur.tolerance_under_kg ?? "—"} / +{cur.tolerance_over_kg ?? "—"} kg
        {(cur.tolerance_under_kg == null || cur.tolerance_over_kg == null) && <b className="ml-2 text-amber-800">{t("cls_policy_not_configured")}</b>}</p>}
      <div className="grid sm:grid-cols-4 gap-3">
        <Inp label={t("target_kg")} type="number" value={f.target_net_kg} onChange={(e) => setF({ ...f, target_net_kg: e.target.value })} testid="policy-target-input" />
        <Inp label={t("tol_under")} type="number" step="0.1" value={f.tolerance_under_kg} onChange={(e) => setF({ ...f, tolerance_under_kg: e.target.value })} testid="policy-under-input" />
        <Inp label={t("tol_over")} type="number" step="0.1" value={f.tolerance_over_kg} onChange={(e) => setF({ ...f, tolerance_over_kg: e.target.value })} testid="policy-over-input" />
        <Inp label={t("notes")} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} testid="policy-notes-input" />
      </div>
      <button className="btn-primary" onClick={save} data-testid="policy-save-button">{t("new_version")}</button>
      <ul className="text-xs text-slate-600">{vers.map((v) => <li key={v.id}>v{v.version} · {v.target_net_kg} · −{v.tolerance_under_kg ?? "—"} / +{v.tolerance_over_kg ?? "—"} · {v.created_by} · {fmtDateTime(v.created_at, lang)}</li>)}</ul>
    </section>
  );
};

const Recipients = () => {
  const { t, te, lang } = useLang();
  const farms = useMaster("farms");
  const [rows, setRows] = useState([]);
  const [runs, setRuns] = useState([]);
  const [f, setF] = useState({ email: "", name: "", farm_id: "" });
  const load = () => { api.get("/email-recipients").then((r) => setRows(r.data)); api.get("/reports/runs").then((r) => setRuns(r.data)); };
  useEffect(() => { load(); }, []);
  const onErr = (e) => toast.error(te(errCode(e)));
  const add = () => api.post("/email-recipients", { ...f, farm_id: f.farm_id || null, is_test: false }).then(() => { setF({ email: "", name: "", farm_id: "" }); load(); }).catch(onErr);
  const del = (id) => api.delete(`/email-recipients/${id}`).then(load).catch(onErr);
  const sendReminder = () => api.post("/reports/dispatch-reminder/send-now").then(() => { toast.success(t("saved")); setTimeout(load, 5000); }).catch(onErr);
  const sendWeekly = () => api.post("/reports/weekly/send-now").then(() => { toast.success(t("saved")); setTimeout(load, 6000); }).catch(onErr);
  const sendNow = () => api.post("/reports/daily/send-now").then(() => { toast.success(t("saved")); setTimeout(load, 4000); }).catch(onErr);
  return (
    <section className="card p-4 space-y-3" data-testid="recipients-section">
      <div className="flex justify-between items-center flex-wrap gap-2"><h2 className="font-bold text-lg">{t("recipients")}</h2>
        <div className="flex gap-2 flex-wrap"><button className="btn-secondary btn-sm" onClick={sendNow} data-testid="send-report-now-button"><Send size={16} />{t("send_now")}</button>
          <button className="btn-secondary btn-sm" onClick={sendWeekly} data-testid="send-weekly-now-button"><Send size={16} />{t("send_weekly_now")}</button>
          <button className="btn-secondary btn-sm" onClick={sendReminder} data-testid="send-dispatch-reminder-button"><Send size={16} />{t("send_dispatch_reminder")}</button></div></div>
      <p className="text-sm text-slate-600" data-testid="weekly-hint">{t("weekly_hint")} {t("dispatch_hint")}</p>
      <div className="grid sm:grid-cols-4 gap-3 items-end">
        <Inp label={t("email")} type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} testid="recipient-email-input" />
        <Inp label={t("name")} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} testid="recipient-name-input" />
        <Sel label={t("farm")} value={f.farm_id} onChange={(v) => setF({ ...f, farm_id: v })} options={farms} placeholder={t("all_farms_opt")} testid="recipient-farm-select" />
        <button className="btn-primary" disabled={!f.email.includes("@")} onClick={add} data-testid="recipient-add-button">{t("add")}</button>
      </div>
      <table className="tbl w-full"><tbody>{rows.map((r) => (
        <tr key={r.id}><td className="font-mono">{r.email}</td><td>{r.name}</td><td>{r.farm_id ? nameOf(farms, r.farm_id) : t("all_farms_opt")}</td><td>{r.is_test && <span className="text-xs font-bold bg-amber-200 px-2 rounded">{t("test_flag")}</span>}</td>
          <td><button onClick={() => del(r.id)} aria-label={t("delete")} data-testid={`recipient-delete-${r.email}`}><Trash2 size={16} /></button></td></tr>))}</tbody></table>
      <p className="eyebrow">{t("runs")}</p>
      <ul className="text-xs space-y-1" data-testid="report-runs">{runs.map((r) => (
        <li key={r.run_id}>{fmtDateTime(r.started_at, lang)} · <b>{t(r.kind === "weekly" ? "run_weekly" : r.kind === "dispatch" ? "run_dispatch" : "run_daily")}</b> · {r.source} · {r.status} · {(r.results || []).filter((x) => x.ok).length}/{(r.results || []).length} OK</li>))}</ul>
    </section>
  );
};

export default function Settings() {
  const { t } = useLang();
  return (
    <div className="space-y-5" data-testid="settings-page">
      <PageTitle>{t("nav_settings")}</PageTitle>
      <Policy />
      <Recipients />
    </div>
  );
}
